import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import Button from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import LabeledValue from '@pelckmans/elementis/components/labeled-value';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Section from '@pelckmans/elementis/components/section';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useToast } from '@pelckmans/elementis/components/toast';
import PlusIcon from '@pelckmans/elementis/icons/plus';
import BrandIcon from '../../../shared-components/BrandIcon';
import SmartschoolIcon from '@pelckmans/elementis/icons/smartschool';
import DeleteIcon from '@pelckmans/elementis/icons/trash';
import sortBy from 'lodash/sortBy';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TABS } from '..';
import {
  deleteSubjectYear,
  deleteUserSchool,
  fetchTeacherProfile,
  SubjectYear,
  TeacherProfileResponse,
} from '../../../api/my-account';
import { TOAST_ID } from '../../../constants/ToastIds';
import AccountContext from '../../../contexts/AccountContext';
import IdentityContext from '../../../contexts/IdentityContext';
import ModalContext from '../../../contexts/ModalContext';
import UserImagesContext from '../../../contexts/UserImagesContext';
import useStudioGroup from '../../../hooks/useStudioGroup';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import ConfirmationModal from '../../../shared-components/ConfirmationModal';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { capitalize } from '../../../utils/string';
import useLoginProvider from '../hooks/useLoginProvider';
import SelectAvatarModal from '../modals/SelectAvatarModal';
import PelckmansIdentity from './PelckmansIdentity';
import SmartschoolIdentity from './SmartschoolIdentity';

const collator = new Intl.Collator('en', { numeric: true });

const sortByName = <T extends { name: string }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => collator.compare(a.name, b.name));

const sortBySubjectName = <T extends { subject: { name: string } }>(arr: T[]) =>
  [...arr].sort((a, b) => collator.compare(a.subject.name, b.subject.name));

const isSmartschoolEnvironment = (studioGroup: StudioGroup) => studioGroup === 'SO' || studioGroup === 'ES';

function MyDetails() {
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();
  const { connect } = useLoginProvider();
  const { avatar } = useContext(UserImagesContext);
  const { addToast } = useToast();
  const { showModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const [teacherProfile, setTeacherProfile] = useState<TeacherProfileResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const studioGroup = useStudioGroup();

  const {
    account: { role, oauthIdentities, smartschoolSchools, verifiedEmail, existingPassword },
  } = useContext(AccountContext);

  const smsIdentities = sortBy(
    (oauthIdentities || [])
      .filter(x => x.provider === 'smartschool')
      .map(x => ({
        ...x,
        school: smartschoolSchools.find(y => x.url.includes(y.domain))!,
      })),
    ['school.name'],
  );

  const sortedSmartschoolSchools = useMemo(() => sortByName(smartschoolSchools), [smartschoolSchools]);

  const {
    identity: { fullName, isTeacherAlike },
  } = useContext(IdentityContext);

  const handleDeleteSubjectYear = async (subjectYear: SubjectYear) => {
    showModal(
      <ConfirmationModal
        id="delete-subject-year-confirmation-modal"
        confirmationText={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.DELETE_SUBJECT_YEAR.ACTIONS.CONFIRM')}
        headerTitle={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.DELETE_SUBJECT_YEAR.TITLE')}
        message={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.DELETE_SUBJECT_YEAR.MESSAGE')}
        onConfirm={async () => {
          try {
            await deleteSubjectYear(subjectYear.subject.id);
            await getTeacherProfile(false);
            addToast({
              id: TOAST_ID.DELETE_SUBJECT_YEAR_SUCCESS,
              variant: 'success',
              title: t('TOAST.SUCCESS.TITLE'),
              children: t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.DELETE_SUBJECT_YEAR.SUCCESS_TOAST.MESSAGE'),
            });
          } catch (e) {
            addToast({
              id: TOAST_ID.DELETE_SUBJECT_YEAR_SUCCESS,
              variant: 'danger',
              title: t('TOAST.DANGER.TITLE'),
              children: t('TOAST.DANGER.MESSAGE'),
            });
          }
        }}
      />,
    );
  };

  const handleDeleteUserSchool = async (schoolId: string) => {
    showModal(
      <ConfirmationModal
        id="delete-user-school-confirmation-modal"
        confirmationText={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.DELETE_SCHOOL.ACTIONS.CONFIRM')}
        headerTitle={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.DELETE_SCHOOL.TITLE')}
        message={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.DELETE_SCHOOL.MESSAGE')}
        onConfirm={async () => {
          try {
            await deleteUserSchool(schoolId);
            await getTeacherProfile(false);
            addToast({
              id: TOAST_ID.DELETE_USER_SCHOOL_SUCCESS,
              variant: 'success',
              title: t('TOAST.SUCCESS.TITLE'),
              children: t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.DELETE_SCHOOL.SUCCESS_TOAST.MESSAGE'),
            });
          } catch (e) {
            addToast({
              id: TOAST_ID.DELETE_USER_SCHOOL_SUCCESS,
              variant: 'danger',
              title: t('TOAST.DANGER.TITLE'),
              children: t('TOAST.DANGER.MESSAGE'),
            });
          }
        }}
      />,
    );
  };

  const getTeacherProfile = useCallback(
    async (showLoader = true) => {
      if (!isTeacherAlike) return;

      if (showLoader) setIsLoading(true);

      try {
        const data = await fetchTeacherProfile();

        const sortedData = {
          ...data,
          schools: sortByName(data.schools),
          subjectYears: sortBySubjectName(data.subjectYears),
        };

        setTeacherProfile(sortedData);
      } catch (e) {
        handleError(e);
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [handleError, isTeacherAlike],
  );

  useEffect(() => {
    getTeacherProfile();
  }, [getTeacherProfile]);

  return (
    <Tabs.TabsContent>
      <Tabs.TabPanel id={TABS.MY_DETAILS}>
        <div className="content">
          <Section title={t('PAGES.PROFILE.MY_DETAILS.PERSONAL_DETAILS.TITLE')}>
            <div className="form-personal-details">
              <Avatar
                buttonProps={{
                  onClick: () => showModal(<SelectAvatarModal id="select-custom-avatar-modal" />),
                  children: t('PAGES.PROFILE.MY_DETAILS.PERSONAL_DETAILS.CHANGE_AVATAR_BUTTON'),
                }}
              >
                <AvatarImage size="xxxl" name="avatar" src={avatar} />
              </Avatar>
              <div className="list-labeled-value">
                <LabeledValue label={t('PAGES.PROFILE.MY_DETAILS.FORM.NAME')} text={fullName} />
                <LabeledValue
                  label={t('PAGES.PROFILE.MY_DETAILS.FORM.ROLE')}
                  pillGroupProps={{
                    role: undefined,
                    'aria-label': undefined,
                    children: <Pill>{capitalize(t(role))}</Pill>,
                  }}
                />
              </div>
            </div>
          </Section>

          <div className="list-sections">
            <Section
              title={t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.TITLE')}
              actions={
                isSmartschoolEnvironment(studioGroup) ? (
                  <Button
                    prominence="secondary"
                    startIcon={<PlusIcon />}
                    onClick={() => {
                      if (verifiedEmail && existingPassword) connect('smartschool', language);
                      else
                        addToast({
                          id: TOAST_ID.ADD_SMARTSCHOOL_ACCOUNT_ERROR,
                          variant: 'warning',
                          title: t('TOAST.WARNING.TITLE'),
                          children: t(
                            'PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.TOASTS.PROFILE_INCOMPLETE_COUPLING.TEXT',
                          ),
                        });
                    }}
                  >
                    {smsIdentities && smsIdentities.length > 0
                      ? t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.ADD_EXTRA_SMARTSCHOOL_ACCOUNT')
                      : t('PAGES.PROFILE.MY_DETAILS.LINKED_ACCOUNTS.ADD_SMARTSCHOOL_ACCOUNT')}
                  </Button>
                ) : undefined
              }
              actionGroupRole={undefined}
              actionGroupAriaLabel={undefined}
            >
              <List>
                <PelckmansIdentity />
                {smsIdentities.map(identity => {
                  return <SmartschoolIdentity identity={identity} key={identity.id} />;
                })}
              </List>
            </Section>

            {isTeacherAlike && (
              <>
                <Section title={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.TITLE')}>
                  <List
                    isLoading={isLoading}
                    loadingLabel={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.LOADING.A11Y_LABEL')}
                    emptyMessage={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.NO_SCHOOLS')}
                  >
                    {teacherProfile?.schools.map(school => (
                      <List.Item
                        icon={<BrandIcon />}
                        key={school.id}
                        title={school.name}
                        subText={`${school.address.streetName} ${school.address.streetNumber}, ${school.address.zipCode} ${school.address.city}`}
                        actionArea={
                          <ButtonGroup
                            {...buildGroupProps(
                              <Button
                                as="button"
                                prominence="neutral"
                                size="sm"
                                aria-label={t('PAGES.PROFILE.MY_DETAILS.SCHOOLS.DELETE_SCHOOL.A11Y_LABEL')}
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteUserSchool(school.id)}
                              />,
                            )}
                            size="sm"
                          />
                        }
                      />
                    ))}
                    {sortedSmartschoolSchools.map(school => (
                      <List.Item
                        icon={<SmartschoolIcon />}
                        key={school.id}
                        title={school.name}
                        subText={school.domain}
                      />
                    ))}
                  </List>
                </Section>
                {(studioGroup === 'SO' || studioGroup === 'ES') && (
                  <Section title={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.TITLE')}>
                    <List
                      isLoading={isLoading}
                      loadingLabel={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.LOADING.A11Y_LABEL')}
                      emptyMessage={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.NO_SUBJECTS')}
                    >
                      {teacherProfile?.subjectYears.flatMap(subjectYear => (
                        <List.Item
                          key={subjectYear.subject.id}
                          title={subjectYear.subject.name}
                          actionArea={
                            <ButtonGroup
                              {...buildGroupProps(
                                <Button
                                  as="button"
                                  prominence="neutral"
                                  size="sm"
                                  startIcon={<DeleteIcon />}
                                  aria-label={t('PAGES.PROFILE.MY_DETAILS.SUBJECTS.DELETE_SUBJECT.A11Y_LABEL')}
                                  onClick={() => handleDeleteSubjectYear(subjectYear)}
                                />,
                              )}
                              size="sm"
                            />
                          }
                        />
                      ))}
                    </List>
                  </Section>
                )}
              </>
            )}
          </div>
        </div>
      </Tabs.TabPanel>
    </Tabs.TabsContent>
  );
}

export default MyDetails;
